import { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Users, KeyRound, Copy, ShieldAlert, Search, MessageCircle, Lock, LockOpen, Check, RefreshCw, Clock, UserPlus , Paperclip, Upload, Trash2, Download, RotateCcw } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth, RUOLI_LABEL } from "@/context/AuthContext";
import { BottomSheet, Empty, DeleteButton, Field, BulkCheck, BulkBar, ConfirmAction, TAGLIE, blurSuTouch, fmtData } from "@/components/Shared";
import { Switch } from "@/components/ui/switch";
import { formattaNome, waNumero } from "@/lib/utils";
import { useRefreshOnPull } from "@/components/PullToRefresh";

export default function Animatori({ mode = "staff" }) {
  const { user } = useAuth();
  const [utenti, setUtenti] = useState([]);
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);
  const [nuovoOpen, setNuovoOpen] = useState(false);
  const [form, setForm] = useState({ nome: "", cognome: "", role: "responsabile", username: "", data_nascita: "" });
  const [saving, setSaving] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [accessoBloccato, setAccessoBloccato] = useState(false);
  const [accessoSaving, setAccessoSaving] = useState(false);
  // Codice di attivazione mostrato una sola volta, subito dopo la generazione
  const [codiceOpen, setCodiceOpen] = useState(null); // { nome, email, codice, scadenza }
  const [confermaReset, setConfermaReset] = useState(false);
  // Registrazione autonoma: coda richieste + impostazioni del modulo pubblico
  const [richieste, setRichieste] = useState([]);
  const [regAperte, setRegAperte] = useState(false);
  const [codiceStagione, setCodiceStagione] = useState("");
  const [regOpen, setRegOpen] = useState(false);
  const [regSaving, setRegSaving] = useState(false);
  const isAdmin = user.role === "admin";
  const isResponsiblesMode = mode === "responsabili";

  const load = useCallback(() => {
    api.get("/utenti").then((r) => setUtenti(r.data));
    api.get("/accesso-staff").then((r) => setAccessoBloccato(!!r.data.bloccato)).catch(() => {});
    api.get("/registrazioni-staff").then((r) => setRichieste(r.data)).catch(() => {});
    api.get("/impostazioni").then((r) => {
      setRegAperte(!!r.data.registrazioni_staff_aperte);
      setCodiceStagione(r.data.codice_registrazione_staff || "");
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useRefreshOnPull(load);

  const toggleAccesso = async (blocca) => {
    setAccessoSaving(true);
    try {
      const { data } = await api.put("/accesso-staff", { bloccato: blocca });
      setAccessoBloccato(!!data.bloccato);
      toast.success(data.bloccato
        ? "Accesso bloccato: animatori e aiuti non possono più entrare"
        : "Accesso riabilitato per animatori e aiuti");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setAccessoSaving(false); }
  };

  const staff = utenti.filter((u) => u.role === "animatore" || u.role === "aiuto_animatore");
  const responsabili = utenti.filter((u) => u.role === "responsabile");
  const base = isResponsiblesMode ? responsabili : staff;
  const q = search.trim().toLowerCase();
  const cercati = q
    ? base.filter((u) => `${u.nome} ${u.cognome} ${u.username}`.toLowerCase().includes(q))
    : base;
  // Chi non è attivo in questa stagione esce dall'elenco di lavoro e va in una
  // sezione a parte. NON viene cancellato né nascosto: chi torna l'anno dopo è
  // la stessa persona, e deve poter essere riattivato con un clic senza
  // perdere storico, password e nome utente.
  const lista = cercati.filter((u) => u.attivo_stagione !== false);
  const inattivi = cercati.filter((u) => u.attivo_stagione === false);

  const riattiva = async (u) => {
    try {
      await api.post(`/utenti/${u.id}/riattiva`);
      toast.success(`${u.nome} ${u.cognome} è di nuovo attivo`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const crea = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Nessun telefono: lo inserisce l'interessato al primo accesso, quando
      // conferma il proprio numero. Inserirlo qui significherebbe registrare
      // un recapito altrui basandosi sul ricordo di chi crea l'account.
      const { data } = await api.post("/utenti", {
        ...form,
        username: form.username || null,
        data_nascita: form.data_nascita || null,
      });
      setNuovoOpen(false);
      setForm({ nome: "", cognome: "", role: "responsabile", username: "", data_nascita: "" });
      // Il codice compare una volta sola: qui, in un riquadro dedicato
      setCodiceOpen({
        nome: `${data.nome} ${data.cognome}`, username: data.username,
        codice: data.codice_attivazione, scadenza: data.codice_scadenza,
      });
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const salvaRegistrazioni = async (aperte, codice) => {
    setRegSaving(true);
    try {
      await api.put("/impostazioni", {
        registrazioni_staff_aperte: aperte,
        codice_registrazione_staff: (codice || "").trim().toUpperCase(),
      });
      setRegAperte(aperte);
      setCodiceStagione((codice || "").trim().toUpperCase());
      toast.success(aperte ? "Registrazioni aperte" : "Registrazioni chiuse");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setRegSaving(false); }
  };

  // Il ruolo lo decide il server dall'età: qui non si sceglie più nulla.
  // Due pulsanti erano due modi di sbagliare un'informazione che il sistema
  // ha già, senza garanzia di coerenza fra un responsabile e l'altro.
  // --- Autorizzazione genitoriale (operatori minorenni) ---
  const [autForm, setAutForm] = useState({});
  const [autSaving, setAutSaving] = useState(false);
  const fileAutRef = useRef(null);

  const minorenneSenzaAut = (u) =>
    u?.autorizzazione_genitoriale?.richiesta && u?.autorizzazione_genitoriale?.acquisita !== true;

  const registraAutorizzazione = async () => {
    const f = autForm;
    if (!f.data) return toast.error("Indica la data dell'autorizzazione");
    if (!(f.nome_genitore || "").trim()) return toast.error("Indica chi ha firmato");
    setAutSaving(true);
    try {
      await api.post(`/utenti/${sel.id}/autorizzazione-genitoriale`, {
        data: f.data, nome_genitore: f.nome_genitore,
      });
      toast.success("Autorizzazione registrata: il suo accesso è ora attivo");
      const { data } = await api.get("/utenti");
      setSel(data.find((u) => u.id === sel.id) || null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setAutSaving(false); }
  };

  const caricaFileAut = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("File troppo grande (max 5 MB)");
    setAutSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/utenti/${sel.id}/autorizzazione-genitoriale/file`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Modulo firmato allegato");
      const { data } = await api.get("/utenti");
      setSel(data.find((u) => u.id === sel.id) || null);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setAutSaving(false); if (fileAutRef.current) fileAutRef.current.value = ""; }
  };

  const scaricaFileAut = async () => {
    try {
      const r = await api.get(`/utenti/${sel.id}/autorizzazione-genitoriale/file`, { responseType: "blob" });
      // Blob temporaneo: si apre in una scheda e si revoca subito, così il
      // documento non resta agganciato alla pagina.
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const eliminaFileAut = async () => {
    try {
      await api.delete(`/utenti/${sel.id}/autorizzazione-genitoriale/file`);
      toast.success("Allegato rimosso");
      const { data } = await api.get("/utenti");
      setSel(data.find((u) => u.id === sel.id) || null);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const approvaRichiesta = async (r) => {
    try {
      const { data } = await api.post(`/registrazioni-staff/${r.id}/approva`, {});
      const ruolo = data.role === "aiuto_animatore" ? "Aiuto animatore" : "Animatore";
      toast.success(
        `${data.nome} ${data.cognome} approvato come ${ruolo} — nome utente: ${data.username}`,
        { duration: 8000 }
      );
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  // Anteprima del ruolo, calcolata con la stessa regola del server: serve solo
  // a non far premere "Approva" alla cieca. La decisione resta del backend.
  const etaOggi = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d)) return null;
    const o = new Date();
    let a = o.getFullYear() - d.getFullYear();
    const m = o.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && o.getDate() < d.getDate())) a--;
    return a;
  };
  const ETA_MIN_ANIMATORE = 14;
  const ruoloPrevisto = (r) => {
    const e = etaOggi(r.data_nascita) ?? r.eta_alla_registrazione ?? null;
    if (e === null) return { etichetta: "Animatore", eta: null, minorenne: false };
    return {
      etichetta: e < ETA_MIN_ANIMATORE ? "Aiuto animatore" : "Animatore",
      eta: e,
      minorenne: e < 18,
    };
  };

  const rifiutaRichiesta = async (r) => {
    try {
      await api.post(`/registrazioni-staff/${r.id}/rifiuta`);
      toast.success("Richiesta rifiutata");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const rigeneraCodice = async () => {
    try {
      const { data } = await api.post(`/utenti/${sel.id}/codice-attivazione`);
      setCodiceOpen({
        nome: `${sel.nome} ${sel.cognome}`, username: sel.username,
        codice: data.codice_attivazione, scadenza: data.codice_scadenza,
      });
      setSel(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const reimpostaAccesso = async () => {
    try {
      const { data } = await api.post(`/utenti/${sel.id}/reimposta-accesso`);
      setCodiceOpen({
        nome: `${sel.nome} ${sel.cognome}`, username: sel.username,
        codice: data.codice_attivazione, scadenza: data.codice_scadenza,
        reset: true, sessioni: data.sessioni_chiuse,
      });
      setSel(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const salvaModifiche = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/utenti/${sel.id}`, {
        nome: sel.nome, cognome: sel.cognome, telefono: sel.telefono || null,
        // Campi del Profilo: l'interessato li compila una volta sola, qui si correggono
        data_nascita: sel.data_nascita || null,
        taglia_maglia: sel.taglia_maglia || null,
        sesso: sel.sesso || null,
        intolleranze: sel.intolleranze ?? "",
        ...(sel.role === "animatore" || sel.role === "aiuto_animatore" ? { role: sel.role } : {}),
      });
      toast.success("Anagrafica aggiornata");
      setSel(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const elimina = async () => {
    try {
      await api.delete(`/utenti/${sel.id}`);
      toast.success("Account eliminato");
      setSel(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const copiaEmail = (username) => {
    navigator.clipboard.writeText(username);
    toast.success("Email copiata");
  };

  return (
    <div data-testid="animatori-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900">{isResponsiblesMode ? "Responsabili" : "Animatori"}</h1>
          <p className="text-sm text-slate-500 font-semibold">{isResponsiblesMode ? "Referenti e account di accesso" : "Staff e account di accesso"}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {utenti.length > 0 && (
            <button className={bulkMode ? "btn-secondary" : "btn-ghost"} onClick={() => { if (bulkMode) { setBulkMode(false); setSelected([]); } else setBulkMode(true); }} data-testid="animatori-bulk-toggle">
              {bulkMode ? "Annulla selezione" : "Seleziona"}
            </button>
          )}
          {/* Solo Responsabili: animatori e aiuti arrivano dalla registrazione */}
          {isAdmin && isResponsiblesMode && (
            <button className="btn-primary" onClick={() => setNuovoOpen(true)} data-testid="nuovo-utente-btn">
              <Plus className="h-4 w-4" /> Nuovo Responsabile
            </button>
          )}
        </div>
      </div>

      {/* Interruttore accesso app per animatori e aiuti (admin + responsabili) */}
      {!isResponsiblesMode && <div className={`card-app p-4 mb-5 flex items-center justify-between gap-3 ${accessoBloccato ? "!border-red-200 !bg-red-50/60" : ""}`} data-testid="accesso-staff-card">
        <div className="min-w-0 flex items-start gap-3">
          <div className={`h-10 w-10 rounded-2xl flex items-center justify-center shrink-0 ${accessoBloccato ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-600"}`}>
            {accessoBloccato ? <Lock className="h-5 w-5" /> : <LockOpen className="h-5 w-5" />}
          </div>
          <div className="min-w-0">
            <p className="font-bold text-slate-800">Accesso app · Animatori e Aiuti</p>
            <p className="text-xs text-slate-500 font-semibold">
              {accessoBloccato
                ? "Bloccato: non possono accedere e vengono disconnessi. Responsabili e Admin restano operativi."
                : "Attivo: animatori e aiuti possono accedere normalmente."}
            </p>
          </div>
        </div>
        <Switch checked={!accessoBloccato} disabled={accessoSaving} onCheckedChange={(v) => toggleAccesso(!v)} data-testid="accesso-staff-switch" />
      </div>}

      {/* Registrazione autonoma: link unico da condividere nel gruppo */}
      {!isResponsiblesMode && <div className="card-app p-4 mb-5" data-testid="registrazione-staff-card">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex items-start gap-3">
            <div className={`h-10 w-10 rounded-2xl flex items-center justify-center shrink-0 ${regAperte ? "bg-sky-100 text-sky-600" : "bg-slate-100 text-slate-400"}`}>
              <UserPlus className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="font-bold text-slate-800">Registrazione animatori</p>
              <p className="text-xs text-slate-500 font-semibold">
                {regAperte
                  ? "Aperta: chi ha il link e il codice può inviare la richiesta."
                  : "Chiusa: nessuno può registrarsi dal link pubblico."}
              </p>
            </div>
          </div>
          <Switch checked={regAperte} disabled={regSaving || !codiceStagione}
            onCheckedChange={(v) => salvaRegistrazioni(v, codiceStagione)} data-testid="registrazioni-switch" />
        </div>

        <button type="button" onClick={() => setRegOpen((o) => !o)}
          className="text-xs font-bold text-sky-600 mt-3" data-testid="registrazione-config-toggle">
          {regOpen ? "Nascondi impostazioni" : "Codice stagione e link da condividere"}
        </button>

        {regOpen && (
          <div className="mt-3 rounded-2xl bg-slate-50 p-3">
            <Field label="Codice della stagione" hint="Parola d'ordine da scrivere nel gruppo: senza, il modulo non si apre">
              <div className="flex gap-2">
                <input className="input-app uppercase font-mono" value={codiceStagione}
                  onChange={(e) => setCodiceStagione(e.target.value.toUpperCase())}
                  placeholder="ES. SUNEIMI26" data-testid="codice-stagione-input" />
                <button type="button" className="btn-secondary !py-2 shrink-0" disabled={regSaving || !codiceStagione.trim()}
                  onClick={() => salvaRegistrazioni(regAperte, codiceStagione)} data-testid="codice-stagione-salva">
                  Salva
                </button>
              </div>
            </Field>
            <button type="button" className="btn-ghost !py-2 text-xs w-full"
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/registrazione`);
                toast.success("Link copiato: incollalo nel gruppo");
              }} data-testid="copia-link-registrazione">
              <Copy className="h-3.5 w-3.5" /> Copia il link di registrazione
            </button>
          </div>
        )}
      </div>}

      {/* Richieste in attesa: il riconoscimento del responsabile è il vero controllo */}
      {!isResponsiblesMode && richieste.length > 0 && (
        <div className="card-app p-4 mb-5" data-testid="richieste-staff">
          <p className="font-display font-extrabold text-slate-900 mb-3 flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-sky-500" /> Richieste di registrazione
            <span className="badge-warn">{richieste.length}</span>
          </p>
          <div className="space-y-2.5">
            {richieste.map((r) => (
              <div key={r.id} className={`rounded-2xl border p-3 ${r.possibile_duplicato ? "border-amber-300 bg-amber-50/60" : "border-slate-100"}`} data-testid={`richiesta-${r.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-bold text-slate-800 truncate">{r.nome} {r.cognome}</p>
                    <p className="text-xs text-slate-500 font-semibold">
                      {r.data_nascita ? fmtData(r.data_nascita) : "—"} · {r.telefono} · {r.sesso} · maglia {r.taglia_maglia}
                    </p>
                    {r.possibile_duplicato && (
                      <p className="text-xs font-bold text-amber-700 mt-1 flex items-start gap-1">
                        <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {r.nota_duplicato || "Possibile doppione: verifica prima di approvare"}
                      </p>
                    )}
                  </div>
                </div>
                {(() => {
                  const p = ruoloPrevisto(r);
                  return (
                    <>
                      <p className="text-xs font-semibold text-slate-500 mt-2" data-testid={`ruolo-previsto-${r.id}`}>
                        Sarà approvato come <b className="text-slate-800">{p.etichetta}</b>
                        {p.eta !== null
                          ? ` (${p.eta} anni)`
                          : " — data di nascita mancante, ruolo correggibile dopo"}
                        {p.minorenne && (
                          <span className="ml-1.5 badge-warn">Minorenne: serve l'autorizzazione di un genitore</span>
                        )}
                      </p>
                      <div className="flex gap-2 flex-wrap mt-2.5">
                        <button type="button" className="btn-primary !py-1.5 text-xs"
                          onClick={() => approvaRichiesta(r)} data-testid={`approva-${r.id}`}>
                          <Check className="h-3.5 w-3.5" /> Approva
                        </button>
                        <button type="button" className="btn-ghost !py-1.5 text-xs text-red-500 ml-auto"
                          onClick={() => rifiutaRichiesta(r)} data-testid={`rifiuta-${r.id}`}>
                          Rifiuta
                        </button>
                      </div>
                    </>
                  );
                })()}
              </div>
            ))}
          </div>
        </div>
      )}

      {bulkMode && (
        <BulkBar count={selected.length} total={lista.length} testId="animatori-bulk-bar"
          onSelectAll={() => setSelected(selected.length === lista.length ? [] : lista.map((u) => u.id))}
          onClear={() => { setBulkMode(false); setSelected([]); }}
          actions={
            <ConfirmAction onConfirm={async () => {
              try {
                const { data } = await api.post("/utenti/bulk/delete", { ids: selected });
                toast.success(`${data.deleted} account eliminati`);
                setBulkMode(false); setSelected([]); load();
              } catch (err) { toast.error(errMsg(err)); }
            }} title={`Eliminare ${selected.length} account?`} description="L'Amministratore non può essere eliminato." testId="animatori-bulk-delete-confirm">
              <button type="button" disabled={selected.length === 0} className="btn-danger disabled:opacity-40 !py-2" data-testid="animatori-bulk-delete-btn">
                Elimina {selected.length > 0 && `(${selected.length})`}
              </button>
            </ConfirmAction>
          } />
      )}

      <div className="relative mb-5">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input type="search" className="input-app !pl-11" placeholder="Cerca per nome, cognome o nome utente..." value={search} onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }} onChange={(e) => setSearch(e.target.value)} data-testid="animatori-search-input" />
      </div>

      {/* Operatori minorenni bloccati: il conteggio sta QUI, dove si gestiscono
          gli animatori, non in Segreteria — è da questa pagina che si apre la
          scheda e si registra l'autorizzazione. */}
      {!isResponsiblesMode && (() => {
        const inAttesa = utenti.filter(minorenneSenzaAut);
        if (inAttesa.length === 0) return null;
        return (
          <div className="rounded-3xl bg-rose-50 border border-rose-200 p-4 mb-5" data-testid="autorizzazioni-banner">
            <p className="flex items-center gap-2 font-bold text-slate-900">
              <ShieldAlert className="h-5 w-5 text-rose-500" />
              {inAttesa.length} operator{inAttesa.length === 1 ? "e minorenne" : "i minorenni"} senza autorizzazione genitoriale
            </p>
            <p className="text-xs text-slate-600 font-semibold mt-1">
              Il loro accesso non funziona finché non registri il modulo firmato. Apri la
              scheda e compila il riquadro in fondo.
            </p>
            <p className="text-xs text-slate-700 font-semibold mt-2">
              {inAttesa.slice(0, 12).map((u) => `${u.nome} ${u.cognome}`).join(" · ")}
              {inAttesa.length > 12 && ` … e altri ${inAttesa.length - 12}`}
            </p>
          </div>
        );
      })()}

      {/* --- NON ATTIVI QUEST'ANNO (lotto 12) ---
          Persone che c'erano ma non sono ancora state segnate per questa
          stagione. Prima l'unico modo di "toglierle" era cancellare l'account,
          che portava via anche lo storico: qui restano, fuori dall'elenco di
          lavoro, e tornano con un clic. */}
      {inattivi.length > 0 && (
        <div className="rounded-3xl bg-slate-50 border border-slate-200 p-4 mb-5"
          data-testid="animatori-inattivi">
          <p className="font-bold text-slate-800">
            {inattivi.length} {inattivi.length === 1 ? "persona non attiva" : "persone non attive"} in questa stagione
          </p>
          <p className="text-xs text-slate-600 font-semibold mt-1 mb-3">
            Il loro account esiste ancora e funziona. Riattivale se partecipano anche
            quest'anno: ritrovano storico, password e nome utente.
          </p>
          <div className="flex flex-wrap gap-2">
            {inattivi.map((u) => (
              <button key={u.id} type="button" className="btn-ghost !py-1.5 text-xs"
                onClick={() => riattiva(u)} data-testid={`riattiva-${u.id}`}>
                <RotateCcw className="h-3.5 w-3.5" /> {u.nome} {u.cognome}
              </button>
            ))}
          </div>
        </div>
      )}

      {lista.length === 0 ? (
        <Empty icon={Users} text={isResponsiblesMode ? "Nessun Responsabile attivo in questa stagione" : "Nessun account attivo in questa stagione"}
          sub={inattivi.length > 0
            ? "Riattiva chi partecipa anche quest'anno dal riquadro qui sopra"
            : (isResponsiblesMode ? "Crea il primo account dal pulsante in alto" : "Approva una richiesta o attendi una nuova registrazione")} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {lista.map((u, i) => {
            const isSel = selected.includes(u.id);
            return (
              <motion.div
                key={u.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.03, 0.4) }}
                onClick={() => bulkMode
                  ? setSelected((s) => s.includes(u.id) ? s.filter((x) => x !== u.id) : [...s, u.id])
                  : (setSel({ ...u }), setConfermaReset(false))}
                className={`card-app p-4 text-left cursor-pointer hover:-translate-y-1 hover:shadow-lg transition-all ${bulkMode && isSel ? "ring-2 ring-amber-400" : ""}`}
                data-testid={`utente-card-${u.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  {bulkMode && <BulkCheck checked={isSel} onClick={() => setSelected((s) => s.includes(u.id) ? s.filter((x) => x !== u.id) : [...s, u.id])} testId={`utente-check-${u.id}`} />}
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-extrabold text-slate-900 truncate">{u.nome} {u.cognome}</p>
                    <p className="text-xs text-slate-400 font-semibold truncate">{u.username}</p>
                    {minorenneSenzaAut(u) && (
                      <span className="badge-neg inline-flex items-center gap-1 mt-1" data-testid={`badge-aut-${u.id}`}>
                        <ShieldAlert className="h-3 w-3" /> Autorizzazione mancante
                      </span>
                    )}
                    {u.telefono && (
                      <p className="text-xs text-slate-500 font-semibold flex items-center gap-1.5 mt-0.5">
                        {u.telefono}
                        <a
                          href={`https://wa.me/${waNumero(u.telefono)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-emerald-500 hover:text-emerald-600 shrink-0"
                          aria-label={`Scrivi su WhatsApp a ${u.nome}`}
                          data-testid={`whatsapp-${u.id}`}
                        >
                          <MessageCircle className="h-4 w-4" />
                        </a>
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <span className={u.role === "responsabile" ? "badge-info" : u.role === "animatore" ? "badge-warn" : "badge-pos"}>
                    {RUOLI_LABEL[u.role]}
                  </span>
                  {u.stato_attivazione !== "attivo" && (
                    <span className="badge-warn inline-flex items-center gap-1" data-testid={`badge-da-attivare-${u.id}`}>
                      <Clock className="h-3 w-3" /> Da attivare
                    </span>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {isResponsiblesMode && <BottomSheet open={nuovoOpen} onOpenChange={setNuovoOpen} title="Nuovo Responsabile"
        description="Riceverai un codice univoco da consegnargli di persona: con quello completerà il proprio account in quattro passaggi."
        testId="nuovo-utente-sheet">
        <form onSubmit={crea}>
          <div className="rounded-2xl bg-sky-50 border border-sky-200 p-3 mb-4 text-xs font-semibold text-sky-800">
            Animatori e aiuti non si creano da qui: si registrano da soli col link, e tu approvi la richiesta.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nome"><input className="input-app" required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} onBlur={(e) => setForm({ ...form, nome: formattaNome(e.target.value) })} data-testid="utente-nome-input" /></Field>
            <Field label="Cognome"><input className="input-app" required value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} onBlur={(e) => setForm({ ...form, cognome: formattaNome(e.target.value) })} data-testid="utente-cognome-input" /></Field>
          </div>
          <Field label="Nome utente"
            hint="È il nome con cui accederà. Se lo lasci vuoto lo proponiamo noi da nome e cognome; potrà usare anche il cellulare.">
            <input className="input-app" value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, "") })}
              placeholder={form.nome && form.cognome
                ? `${form.nome}.${form.cognome}`.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9._-]/g, "").slice(0, 24)
                : "mario.rossi"}
              autoCapitalize="none" autoCorrect="off" spellCheck="false" maxLength={24}
              data-testid="utente-username-input" />
          </Field>
          <Field label="Data di nascita (facoltativa)" hint="Serve a verificare la maggiore età">
            <input type="date" className="input-app" value={form.data_nascita}
              onChange={(e) => setForm({ ...form, data_nascita: e.target.value })}
              data-testid="utente-nascita-input" />
          </Field>
          {/* La presa visione NON si dichiara qui al posto suo: sarebbe una
              firma per conto terzi. La prende lui al primo accesso. */}
          <p className="text-xs text-slate-500 font-semibold rounded-2xl bg-slate-50 border border-slate-200 p-3 mb-3">
            Al primo accesso inserirà il codice, sceglierà la password, completerà i dati personali,
            indicherà eventuali allergie e leggerà l'informativa privacy. Non serve che tu inserisca nulla al posto suo.
          </p>
          <button className="btn-primary w-full mt-2" disabled={saving} data-testid="utente-submit-btn">{saving ? "Creazione..." : "Crea Responsabile"}</button>
        </form>
      </BottomSheet>}

      <BottomSheet open={!!sel} onOpenChange={(v) => { if (!v) { setSel(null); setConfermaReset(false); setAutForm({}); } }} title={sel ? `${sel.nome} ${sel.cognome}` : ""} description={sel?.username} testId="utente-detail-sheet">
        {sel && (
          <form onSubmit={salvaModifiche}>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className={sel.role === "responsabile" ? "badge-info" : "badge-warn"}>{RUOLI_LABEL[sel.role]}</span>
              {sel.stato_attivazione !== "attivo" && <span className="badge-warn">In attesa di attivazione</span>}
              <button type="button" className="btn-ghost !py-1.5 !px-3 text-xs" onClick={() => copiaEmail(sel.username)} data-testid="copia-email-btn"><Copy className="h-3 w-3" /> Copia nome utente</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nome"><input className="input-app" required value={sel.nome} onChange={(e) => setSel({ ...sel, nome: e.target.value })} onBlur={(e) => setSel({ ...sel, nome: formattaNome(e.target.value) })} data-testid="detail-nome-input" /></Field>
              <Field label="Cognome"><input className="input-app" required value={sel.cognome} onChange={(e) => setSel({ ...sel, cognome: e.target.value })} onBlur={(e) => setSel({ ...sel, cognome: formattaNome(e.target.value) })} data-testid="detail-cognome-input" /></Field>
            </div>
            {(sel.role === "animatore" || sel.role === "aiuto_animatore") && (
              <Field label="Tipo">
                <select className="input-app" value={sel.role} onChange={(e) => setSel({ ...sel, role: e.target.value })} data-testid="detail-role-select">
                  <option value="animatore">Animatore</option>
                  <option value="aiuto_animatore">Aiuto Animatore</option>
                </select>
              </Field>
            )}
            <Field label="Telefono"><input className="input-app" value={sel.telefono || ""} onChange={(e) => setSel({ ...sel, telefono: e.target.value })} data-testid="detail-telefono-input" /></Field>

            {/* Dati compilati dall'interessato nel suo Profilo: bloccati per lui, correggibili qui */}
            <p className="label-app mt-1">Dati dal Profilo</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Data di nascita">
                <input type="date" className="input-app" value={sel.data_nascita || ""} onChange={(e) => setSel({ ...sel, data_nascita: e.target.value })} data-testid="detail-nascita-input" />
              </Field>
              <Field label="Taglia maglia">
                <select className="input-app" value={sel.taglia_maglia || ""}
                  onChange={(e) => { setSel({ ...sel, taglia_maglia: e.target.value }); blurSuTouch(e); }}
                  data-testid="detail-taglia-select">
                  <option value="">Non indicata</option>
                  {TAGLIE.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Sesso" hint="Serve alla generazione delle stanze del camposcuola">
              <select className="input-app" value={sel.sesso || ""}
                onChange={(e) => { setSel({ ...sel, sesso: e.target.value }); blurSuTouch(e); }}
                data-testid="detail-sesso-select">
                <option value="">Non indicato</option>
                <option value="M">Maschile (M)</option>
                <option value="F">Femminile (F)</option>
              </select>
            </Field>
            <Field label="Intolleranze e allergie">
              <textarea className="input-app" rows={2} value={sel.intolleranze || ""} onChange={(e) => setSel({ ...sel, intolleranze: e.target.value })} data-testid="detail-intolleranze-input" />
            </Field>
            <button className="btn-primary w-full mb-3" disabled={saving} data-testid="detail-salva-btn">{saving ? "Salvataggio..." : "Salva modifiche"}</button>

            {/* --- AUTORIZZAZIONE GENITORIALE (solo operatori minorenni) --- */}
            {sel.autorizzazione_genitoriale?.richiesta && (
              <div className={`rounded-2xl border p-4 mb-3 ${minorenneSenzaAut(sel) ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="autorizzazione-panel">
                <p className="flex items-center gap-2 text-sm font-bold text-slate-800 mb-1">
                  <ShieldAlert className={`h-4 w-4 ${minorenneSenzaAut(sel) ? "text-rose-500" : "text-emerald-600"}`} />
                  Autorizzazione genitoriale
                </p>

                {minorenneSenzaAut(sel) ? (
                  <>
                    <p className="text-xs text-slate-700 font-medium leading-relaxed mb-3">
                      Operatore minorenne{sel.eta_alla_registrazione ? ` (${sel.eta_alla_registrazione} anni alla registrazione)` : ""}.
                      <b> Finché non registri l'autorizzazione, il suo accesso non funziona.</b>
                    </p>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <Field label="Data del modulo firmato">
                        <input type="date" className="input-app" value={autForm.data || ""}
                          onChange={(e) => setAutForm({ ...autForm, data: e.target.value })}
                          data-testid="aut-data-input" />
                      </Field>
                      <Field label="Chi ha firmato">
                        <input className="input-app" placeholder="Nome e cognome del genitore"
                          value={autForm.nome_genitore || ""}
                          onChange={(e) => setAutForm({ ...autForm, nome_genitore: e.target.value })}
                          data-testid="aut-nome-input" />
                      </Field>
                    </div>
                    <button type="button" className="btn-primary !py-2 text-xs w-full"
                      onClick={registraAutorizzazione} disabled={autSaving} data-testid="aut-registra-btn">
                      <Check className="h-3.5 w-3.5" /> {autSaving ? "Salvataggio..." : "Registra autorizzazione"}
                    </button>
                  </>
                ) : (
                  <div className="text-xs text-slate-700 font-semibold space-y-0.5 mb-3">
                    <p>Acquisita il <b>{sel.autorizzazione_genitoriale.data ? fmtData(sel.autorizzazione_genitoriale.data) : "—"}</b></p>
                    <p>Firmata da <b>{sel.autorizzazione_genitoriale.nome_genitore || "—"}</b></p>
                    <p className="text-slate-500">Modalità: modulo cartaceo</p>
                  </div>
                )}

                {/* Allegato: la copia del modulo firmato. Non è pubblico, si
                    scarica solo da qui e viene cancellato con l'account. */}
                <div className="border-t border-slate-200/70 pt-3 mt-3">
                  <p className="text-xs font-bold text-slate-600 mb-2">Copia del modulo firmato</p>
                  {sel.autorizzazione_genitoriale?.file_presente ? (
                    <>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Paperclip className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                        <span className="text-xs font-semibold text-slate-700 truncate min-w-0 flex-1">
                          {sel.autorizzazione_genitoriale.file_nome_originale}
                        </span>
                        <button type="button" className="btn-ghost !py-1.5 !px-3 text-xs"
                          onClick={scaricaFileAut} data-testid="aut-file-apri">
                          <Download className="h-3 w-3" /> Apri
                        </button>
                        <button type="button" className="btn-ghost !py-1.5 !px-3 text-xs text-red-500"
                          onClick={eliminaFileAut} data-testid="aut-file-elimina">
                          <Trash2 className="h-3 w-3" /> Rimuovi
                        </button>
                      </div>
                      <p className="text-[11px] text-slate-400 font-semibold mt-1">
                        Caricata il {sel.autorizzazione_genitoriale.file_caricato_il
                          ? new Date(sel.autorizzazione_genitoriale.file_caricato_il).toLocaleDateString("it-IT")
                          : "—"}
                        {" · "}
                        {Math.round((sel.autorizzazione_genitoriale.file_dimensione || 0) / 1024)} KB
                      </p>
                    </>
                  ) : (
                    <>
                      <input ref={fileAutRef} type="file" className="hidden"
                        accept="image/jpeg,image/png,image/webp,application/pdf"
                        onChange={(e) => caricaFileAut(e.target.files?.[0])}
                        data-testid="aut-file-input" />
                      <button type="button" className="btn-ghost !py-2 text-xs"
                        onClick={() => fileAutRef.current?.click()} disabled={autSaving}
                        data-testid="aut-file-carica">
                        <Upload className="h-3.5 w-3.5" /> {autSaving ? "Caricamento..." : "Allega foto o scansione"}
                      </button>
                    </>
                  )}
                  <p className="text-[11px] text-slate-500 font-semibold mt-2 leading-relaxed">
                    JPG, PNG, WEBP o PDF, max 5 MB. I metadati della foto (posizione GPS, modello
                    del telefono) vengono rimossi in automatico. Il file non è pubblico: si apre
                    solo da qui, e viene cancellato insieme all'account.
                  </p>
                </div>
              </div>
            )}

            {/* Gestione dell'accesso: codice di attivazione o ripartenza da zero */}
            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-3 mb-3">
              <p className="text-xs font-bold text-slate-600 mb-2">Accesso all'app</p>
              {sel.stato_attivazione === "attivo" ? (
                <>
                  <p className="text-xs text-slate-500 font-semibold mb-2.5">
                    Account attivo. Se ha perso la password, reimposta l'accesso: riceverà un nuovo codice da
                    consegnare di persona e verrà disconnesso da tutti i dispositivi.
                  </p>
                  {/* Conferma IN LINEA, non un dialog: un modale Radix dentro
                      il BottomSheet (vaul) verrebbe smontato insieme allo sheet
                      e lascerebbe l'interfaccia bloccata. Stesso criterio di
                      DeleteButton. */}
                  {confermaReset ? (
                    <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3" data-testid="reimposta-conferma-inline">
                      <p className="text-xs font-bold text-amber-800 mb-2.5">
                        La password verrà azzerata e tutte le sessioni chiuse.
                        Riceverai un nuovo codice da consegnargli di persona. Procedere?
                      </p>
                      <div className="flex gap-2 justify-end">
                        <button type="button" className="btn-ghost !py-1.5 text-xs" onClick={() => setConfermaReset(false)} data-testid="reimposta-annulla-btn">
                          Annulla
                        </button>
                        <button type="button" className="btn-danger !py-1.5 text-xs" onClick={() => { setConfermaReset(false); reimpostaAccesso(); }} data-testid="reimposta-conferma-btn">
                          <RefreshCw className="h-3.5 w-3.5" /> Sì, reimposta
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="btn-secondary !py-2 text-xs" onClick={() => setConfermaReset(true)} data-testid="reimposta-accesso-btn">
                      <RefreshCw className="h-3.5 w-3.5" /> Reimposta accesso
                    </button>
                  )}
                </>
              ) : (
                <>
                  <p className="text-xs text-slate-500 font-semibold mb-2.5">
                    In attesa di attivazione. Genera un codice e consegnaglielo di persona: lo userà su
                    «Hai un codice di attivazione?» per completare i quattro passaggi del primo accesso.
                  </p>
                  <button type="button" className="btn-secondary !py-2 text-xs" onClick={rigeneraCodice} data-testid="rigenera-codice-btn">
                    <KeyRound className="h-3.5 w-3.5" /> Genera nuovo codice
                  </button>
                </>
              )}
            </div>

            <div className="flex justify-end">
              <DeleteButton onConfirm={elimina} entity={`l'account di ${sel.nome} ${sel.cognome}`} testId="utente-delete-btn" />
            </div>
          </form>
        )}
      </BottomSheet>

      {/* Riquadro del codice di attivazione: compare UNA SOLA VOLTA, subito
          dopo la generazione. Non è recuperabile in seguito e non appare mai
          nelle liste, così non può finire in uno screenshot condiviso. */}
      <BottomSheet open={!!codiceOpen} onOpenChange={(v) => !v && setCodiceOpen(null)}
        title={codiceOpen?.reset ? "Codice per reimpostare la password" : "Codice di attivazione"}
        description="Consegnalo di persona all'interessato: non sarà più visibile dopo aver chiuso questa finestra."
        testId="codice-attivazione-sheet">
        {codiceOpen && <RiquadroCodice dati={codiceOpen} onChiudi={() => setCodiceOpen(null)} />}
      </BottomSheet>
    </div>
  );
}

/** Codice in evidenza, leggibile a voce e copiabile con un tocco. */
function RiquadroCodice({ dati, onChiudi }) {
  const [copiato, setCopiato] = useState(false);

  const copia = async () => {
    try {
      await navigator.clipboard.writeText(dati.codice);
      setCopiato(true);
      setTimeout(() => setCopiato(false), 2500);
    } catch { toast.error("Copia non riuscita: trascrivi il codice a mano"); }
  };

  const scadenza = dati.scadenza
    ? new Date(dati.scadenza).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })
    : null;

  return (
    <div>
      <div className="rounded-3xl border-2 border-amber-300 bg-amber-50 p-5 text-center mb-4">
        <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-1">{dati.nome}</p>
        <p className="text-[11px] font-semibold text-amber-600 mb-3">{dati.username}</p>
        <p className="font-mono font-extrabold text-3xl sm:text-4xl tracking-[0.2em] text-slate-900 select-all break-all" data-testid="codice-attivazione-valore">
          {dati.codice}
        </p>
        <button type="button" onClick={copia} className="btn-primary !py-2 text-xs mx-auto mt-4" data-testid="codice-copia-btn">
          {copiato ? <><Check className="h-4 w-4" /> Copiato</> : <><Copy className="h-4 w-4" /> Copia codice</>}
        </button>
      </div>

      <div className="rounded-2xl bg-slate-50 p-3 mb-4 space-y-1.5">
        {scadenza && (
          <p className="text-xs font-semibold text-slate-600 flex items-start gap-1.5">
            <Clock className="h-3.5 w-3.5 shrink-0 mt-0.5 text-slate-400" /> Valido fino al {scadenza}. Scaduto, se ne genera un altro dalla scheda.
          </p>
        )}
        {dati.reset && (
          <p className="text-xs font-semibold text-slate-600 flex items-start gap-1.5">
            <RefreshCw className="h-3.5 w-3.5 shrink-0 mt-0.5 text-slate-400" />
            {dati.sessioni > 0 ? `${dati.sessioni} session${dati.sessioni === 1 ? "e chiusa" : "i chiuse"}: ` : ""}
            dovrà reimpostare solo la password con questo codice: i dati personali e la privacy restano invariati.
          </p>
        )}
        <p className="text-xs font-semibold text-slate-600 flex items-start gap-1.5">
          <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-0.5 text-slate-400" /> Consegnalo a voce o di persona: evita screenshot in chat di gruppo.
        </p>
      </div>

      <button type="button" className="btn-primary w-full" onClick={onChiudi} data-testid="codice-chiudi-btn">
        Ho consegnato il codice
      </button>
    </div>
  );
}
